import { createGetter, createSetter } from '@/shared/js/utils/vuexUtilities'
import { LocationService } from '@/shared/js/services/LocationService'
import { persist, load } from '@/shared/js/utils'
import { IdleVisibiltyService } from '@/shared/js/services/IdleVisibiltyService'
import MessageBox from 'element-ui/lib/message-box'

const COMMIT_HASH = process.env.VUE_APP_COMMIT_REF

// Hostnames a local dev server is reachable on. A deployed build is always served
// from a real domain (staging-hypenews / hypenews .hypefactors.com), so this is the
// reliable signal for "am I a local dev server". NODE_ENV can't be used here: the
// `dev:staging` script runs with NODE_ENV=production (see .env.staging), so the
// client bundle sees `process.env.NODE_ENV === 'production'` exactly like a deployed
// build does.
const LOCAL_DEV_HOSTS = ['localhost', '127.0.0.1', '0.0.0.0', '']

export default function updateChecker ({ Api, Trans }) {
  const state = () => ({
    hash: '',
    isOutdated: false
  })

  const getters = {
    isOutdated: createGetter('isOutdated')
  }

  const mutations = {
    STORE_HASH: createSetter('hash'),
    SET_OUTDATED: createSetter('isOutdated', true)
  }

  const actions = {
    /**
     * Inits the idle detection.
     * Ran once just before the app is booted in `main.js`
     */
    init ({ commit, dispatch }) {
      // Never run the version check against a local dev server. Its build-time
      // commit ref (git HEAD) never matches the served /deployment.json, so the
      // "new version available" MessageBox fired on every local session and
      // intercepted all pointer events. Deployed builds run on a real domain and
      // are still version-checked; only localhost dev servers are skipped.
      if (LOCAL_DEV_HOSTS.includes(LocationService.hostname())) return
      // if the hash is not available, bail
      if (process.env.VUE_APP_ENV === 'development' || !COMMIT_HASH) return
      // store the hash at build time
      commit('STORE_HASH', COMMIT_HASH)

      // set the idle time to 2 hours
      IdleVisibiltyService.on('wakeup', () => {
        // go back updating data
        dispatch('checkFreshness')
      })
      // Check the freshness at start
      dispatch('checkFreshness')
    },
    /**
     * Fetches the current hash
     * @return {Promise<String>}
     */
    async fetchHash () {
      const { data } = await Api.get('/deployment.json', {
        params: { stamp: Date.now() },
        baseURL: null
      })
      return data
    },
    /**
     * Checks if the currently saved commit hash is outdated
     * @return {Promise<void>}
     */
    async checkFreshness ({ dispatch, state }) {
      try {
        const hash = await dispatch('fetchHash')
        if (hash && state.hash !== hash) {
          dispatch('handleOutdatedVersion')
        }
      } catch (err) {
        console.log(err)
      }
    },
    /**
     * Handles the app when it is outdated
     * @return {Promise<void>}
     */
    async handleOutdatedVersion ({ commit, state }) {
      // if we are already outdated, we dont need to ask the user again.
      if (state.isOutdated) return

      // The `isOutdated` guard above is per-app-boot and resets on any hard
      // navigation/reload. If the user previously dismissed the prompt with
      // "Cancel" we persisted FORCE_RELOAD so the router guard reloads on the
      // next navigation. That intent survives the boot: if it is still set,
      // the user already made their choice — don't re-nag, just mark outdated
      // and let the router guard silently complete the reload.
      if (load('FORCE_RELOAD', false)) {
        commit('SET_OUTDATED')
        return
      }

      commit('SET_OUTDATED')
      // Alert the user to refresh immediately
      try {
        // If they click OK, refresh
        await MessageBox.confirm(Trans.t('warnings.new_version_available', { client: process.env.VUE_APP_NAME }))
        persist('FORCE_RELOAD', false)
        LocationService.reload()
      } catch (e) {
        console.log(e)
        // if they clicked cancel, we refresh on next page navigation
        persist('FORCE_RELOAD', true)
      }
    }
  }

  return {
    state,
    getters,
    mutations,
    actions,
    namespaced: true
  }
}
